import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { RealtimeClient, ItemType } from '../lib/realtimeGa';
import { WavRecorder, WavStreamPlayer } from '../lib/wavtools/index.js';
import { X } from 'react-feather';
import { Sparkle } from 'lucide-react';
import WebCam from '../components/WebCam';
import { Modal, message } from 'antd';
import { Link } from 'react-router-dom';
import { Settings, AlertOctagon } from 'lucide-react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { decryptStudentId } from '../utils/cryptoUtils.js';
 
interface RealtimeEvent {
  time: string;
  source: 'client' | 'server';
  count?: number;
  event: { [key: string]: any };
}

interface Education {
  branch: string;
  description?: string;
}

interface Experience {
  role: string;
  description?: string;
}

interface Certificate {
  name: string;
  issuedOrganization?: string;
}

interface Project {
  name: string;
  description?: string;
}

interface Activity {
  name: string;
}

interface ResumeData {
  name: string;
  email: string;
  educations: Education[];
  experiences: Experience[];
  certificates: Certificate[];
  projects: Project[];
  activities: Activity[];
  skills: string[];
}

interface MediaRecorderRef {
  mediaRecorder: MediaRecorder | null;
  recordedChunks: Blob[];
  uploadPromises: Promise<void>[];
  videoStream: MediaStream | null;
  audioContext: AudioContext | null;
  mixedStream: MediaStream | null;
}

interface AudioQueueItem {
  id: string;
  endTime: number;
}

export function Interview() {
  const [instructions, setInstructions] = useState<string>('');
  const [resumeContent, setResumeContent] = useState<string>('');
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const currentAudioRef = useRef<string | null>(null);
  const lastUserSpeechEndRef = useRef<number>(0);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');
  const [alertCount, setAlertCount] = useState<number>(0);

  const handleAlertCount = (count: number) => {
    setAlertCount(count);
  };

  const [sessionId] = useState(crypto.randomUUID());


  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorderRef>({
    mediaRecorder: null,
    recordedChunks: [],
    uploadPromises: [],
    videoStream: null,
    audioContext: null,
    mixedStream: null,
  });

  const aiAudioDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(
    null
  );
  const microphoneSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const aiAudioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const setupAudioMixing = async (microphoneStream: MediaStream) => {
    try {
      // Create audio context
      const audioContext = new AudioContext();

      await audioContext.resume(); // Ensure context is running

      mediaRecorderRef.current.audioContext = audioContext;

      // Create destination for mixed audio
      const destination = audioContext.createMediaStreamDestination();
      aiAudioDestinationRef.current = destination;

      // Create source from microphone
      const micSource = audioContext.createMediaStreamSource(microphoneStream);
      microphoneSourceRef.current = micSource;

      // Connect microphone to destination
      micSource.connect(destination);

      // console.log('Audio mixing setup complete');
      return destination.stream;
    } catch (error) {
      console.error('Error setting up audio mixing:', error);
      return microphoneStream; // Fallback to original stream
    }
  };

  // 2. Corrected addAIAudioToMix function
  const addAIAudioToMix = async (audioData: Int16Array) => {
    try {
      const audioContext = mediaRecorderRef.current.audioContext;
      if (!audioContext || !aiAudioDestinationRef.current) {
        // console.warn('Audio context not available for mixing');
        return;
      }

      // Convert Int16Array to Float32Array
      const float32Array = new Float32Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        float32Array[i] = audioData[i] / 32768.0; // Convert to -1 to 1 range
      }

      // Create audio buffer
      const audioBuffer = audioContext.createBuffer(
        1,
        float32Array.length,
        24000
      );
      audioBuffer.copyToChannel(float32Array, 0);

      // Create buffer source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;

      // Create gain node for volume control
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0.8; // Adjust AI audio volume

      // Connect: source -> gain -> destination
      source.connect(gainNode);
      gainNode.connect(aiAudioDestinationRef.current);

      // Play the AI audio
      source.start();
    } catch (error) {
      console.error('Error adding AI audio to mix:', error);
    }
  };

  // Function to start recording
  const startRecording = async () => {
    try {
      // Get camera and microphone
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: false, // Important: disable echo cancellation
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      // Setup audio mixing FIRST
      const mixedAudioStream = await setupAudioMixing(videoStream);

      // Get video track from original stream
      const videoTrack = videoStream.getVideoTracks()[0];
      const audioTrack = mixedAudioStream.getAudioTracks()[0];

      // Create combined stream
      const combinedStream = new MediaStream([videoTrack, audioTrack]);

      // Store the streams for later cleanup
      mediaRecorderRef.current.videoStream = videoStream;
      mediaRecorderRef.current.mixedStream = combinedStream;

      // Initialize MediaRecorder with better settings
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
        videoBitsPerSecond: 500000,
        audioBitsPerSecond: 128000,
      });

      // Store the MediaRecorder
      mediaRecorderRef.current.mediaRecorder = mediaRecorder;
      mediaRecorderRef.current.recordedChunks = [];
      mediaRecorderRef.current.uploadPromises = [];

      // Upload each 30s chunk
      let partNumber = 1;
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          const chunkPartNumber = partNumber;
          mediaRecorderRef.current.recordedChunks.push(event.data);
          const uploadPromise = uploadChunk(event.data, chunkPartNumber);
          mediaRecorderRef.current.uploadPromises.push(uploadPromise);
          partNumber++;
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
      };

      // Start recording and emit data every 30s
      mediaRecorder.start(30000);
      setIsRecording(true);
      // console.log('Recording started with AI audio mixing enabled');
    } catch (error) {
      console.error('Error starting recording:', error);
      alert(
        'Failed to start recording. Please check your camera and microphone permissions.'
      );
    }
  };

  const [isUploading, setIsUploading] = useState(false); // Add state for loading

  const uploadChunk = async (chunk: any, partNumber: any) => {
    const formData = new FormData();
    formData.append('video', chunk, `interview-recording-part${partNumber}.webm`);
    formData.append('studentId', studentId);
    formData.append('sessionId', sessionId);
    formData.append('partNumber', String(partNumber));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/recordings/uploadChunk`,
        {
          method: 'POST',
          body: formData,
        }
      );
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || `Upload failed for chunk ${partNumber}`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      throw err;
    }
  };

  // Function to stop recording and upload to gcp
  const stopRecordingAndUpload = async () => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const {
          mediaRecorder,
          uploadPromises,
          videoStream,
          audioContext,
          mixedStream,
        } = mediaRecorderRef.current;

        if (!mediaRecorder) {
          resolve();
          return;
        }

        // Set up the event to trigger when recording finishes
        mediaRecorder.onstop = async () => {
          try {
            // Show loading indicator while uploading
            setIsUploading(true);
            await Promise.all(uploadPromises);

            // Clean up audio context
            if (audioContext && audioContext.state !== 'closed') {
              await audioContext.close();
            }

            // Clean up streams
            if (videoStream) {
              videoStream.getTracks().forEach((track) => track.stop());
            }
            if (mixedStream) {
              mixedStream.getTracks().forEach((track) => track.stop());
            }

            // Reset refs
            aiAudioDestinationRef.current = null;
            microphoneSourceRef.current = null;
            aiAudioSourceRef.current = null;

            setIsRecording(false);
            setIsUploading(false);
            // console.log('Recording stopped and uploaded with AI audio');
            resolve();
          } catch (error) {
            console.error('Error in onstop handler:', error);
            reject(error);
          }
        };

        // Stop the recording
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping recording:', error);
        reject(error);
      }
    });
  };

  /**
   * Instantiate:
   * - WavRecorder (speech input)
   * - WavStreamPlayer (speech output)
   * - RealtimeClient (API client)
   */
  const wavRecorderRef = useRef<WavRecorder>(
    new WavRecorder({ sampleRate: 24000 })
  );
  const wavStreamPlayerRef = useRef<WavStreamPlayer>(
    new WavStreamPlayer({ sampleRate: 24000 })
  );

  // Start with no client
  const clientRef = useRef<RealtimeClient | null>(null);

  // Function to ensure client exists

  async function ensureClient() {
    // Always create a fresh client to avoid stale connections
    if (clientRef.current) {
      try {
        await clientRef.current.disconnect();
      } catch (error) {
        console.log('Error disconnecting old client:', error);
      }
      clientRef.current = null;
    }

    try {
      // Fetch fresh ephemeral key from your backend
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/realtime/token`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to get token: ${res.status} ${res.statusText}`);
      }

      const { client_secret } = await res.json();

      if (!client_secret) {
        throw new Error('No client_secret received from server');
      }

      const client = new RealtimeClient({
        apiKey: client_secret, // ek_...
        dangerouslyAllowAPIKeyInBrowser: true, // required in browser
        debug: false,
      });

      // Set up event listeners
      client.on('error', (event: any) => {
        console.error('RealtimeClient error:', event);
      });

      client.on('conversation.updated', () => {
        setItems(client.conversation.getItems());
      });

      clientRef.current = client;
      return client;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  }

  /**
   * References for
   * - Rendering audio visualization (canvas)
   * - Autoscrolling event logs
   * - Timing delta for event log displays
   */
  const clientCanvasRef = useRef<HTMLCanvasElement>(null);
  const serverCanvasRef = useRef<HTMLCanvasElement>(null);
  const eventsScrollHeightRef = useRef(0);
  const eventsScrollRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<string>(new Date().toISOString());

  /**
   * All of our variables for displaying application state
   * - items are all conversation items (dialog)
   * - realtimeEvents are event logs, which can be expanded
   * - memoryKv is for set_memory() function
   * - coords, marker are for get_weather() function
   */
  const [items, setItems] = useState<ItemType[]>([]);
  const [realtimeEvents, setRealtimeEvents] = useState<RealtimeEvent[]>([]);

  const [isConnected, setIsConnected] = useState(false);
  const [memoryKv, setMemoryKv] = useState<{ [key: string]: any }>({});

  // Add this near other state declarations
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  const INACTIVITY_TIMEOUT = 60000; // 30 seconds of inactivity before prompting

  /**
   * Connect to conversation:
   * WavRecorder taks speech input, WavStreamPlayer output, client is API client
   */

  // decrypt logic implementaion

  // UNCOMMETED - TEST PURPOSE


  const [studentId, setStudentId] = useState('');
  const { encryptId } = useParams(); // Get encrypted ID from URL

  // UNCOMMETED - TEST PURPOSE

  useEffect(() => {
    const decryptId = async () => {
      if (encryptId) {
        const decryptedResult = await decryptStudentId(encryptId);
        setStudentId(decryptedResult);
      }
    };

    decryptId();
  }, [encryptId]);

  // Modify the useEffect for fetching resume data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get<ResumeData>(
          `${process.env.REACT_APP_API_BASE_URL}/api/student/verified/${studentId}`
        );

        // Format the resume data in a structured, readable format for the AI
        const resumeData = response.data;
        let formattedResume = '';

        // Add basic info
        formattedResume += `Name: ${resumeData.name || 'N/A'}\n`;
        formattedResume += `Email: ${resumeData.email || 'N/A'}\n\n`;

        // Add education section
        formattedResume += 'EDUCATION\n';
        if (resumeData.educations && resumeData.educations.length > 0) {
          resumeData.educations.forEach((edu) => {
            formattedResume += `- ${edu.branch || 'N/A'}\n`;
            if (edu.description) {
              formattedResume += `  ${edu.description.trim()}\n`;
            }
          });
        } else {
          formattedResume += '- No education details provided\n';
        }
        formattedResume += '\n';

        // Add experience section
        formattedResume += 'WORK EXPERIENCE\n';
        if (resumeData.experiences && resumeData.experiences.length > 0) {
          resumeData.experiences.forEach((exp) => {
            formattedResume += `- ${exp.role || 'N/A'}\n`;
            if (exp.description) {
              formattedResume += `  ${exp.description.trim()}\n`;
            }
          });
        } else {
          formattedResume += '- No experience details provided\n';
        }
        formattedResume += '\n';

        // Add projects section
        formattedResume += 'PROJECTS\n';
        if (resumeData.projects && resumeData.projects.length > 0) {
          resumeData.projects.forEach((proj) => {
            formattedResume += `- ${proj.name || 'N/A'}\n`;
            if (proj.description) {
              formattedResume += `  ${proj.description.trim()}\n`;
            }
          });
        } else {
          formattedResume += '- No project details provided\n';
        }
        formattedResume += '\n';

        // Add certificates section
        formattedResume += 'CERTIFICATIONS\n';
        if (resumeData.certificates && resumeData.certificates.length > 0) {
          resumeData.certificates.forEach((cert) => {
            formattedResume += `- ${cert.name || 'N/A'}`;
            if (cert.issuedOrganization) {
              formattedResume += ` (${cert.issuedOrganization})\n`;
            } else {
              formattedResume += '\n';
            }
          });
        } else {
          formattedResume += '- No certification details provided\n';
        }
        formattedResume += '\n';

        // Add skills section
        formattedResume += 'SKILLS\n';
        if (resumeData.skills && resumeData.skills.length > 0) {
          resumeData.skills.forEach((skill) => {
            formattedResume += `- ${skill}\n`;
          });
        } else {
          formattedResume += '- No skills provided\n';
        }

        // Set the formatted resume content
        setResumeContent(formattedResume);
        // console.log('Formatted Resume:', formattedResume);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [studentId]);

  const connectConversation = useCallback(async () => {
    // Disconnect existing connection if any
    if (clientRef.current && isConnected) {
      try {
        await clientRef.current.disconnect();
      } catch (error) {
        console.log('Error disconnecting existing client:', error);
      }
      setIsConnected(false);
      clientRef.current = null;
    }

    try {
      // Get fresh client with new ephemeral token
      const client = await ensureClient();

      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      startTimeRef.current = new Date().toISOString();

      // Request media permissions
      const [cameraPermission, micPermission] = await Promise.all([
        navigator.mediaDevices.getUserMedia({ video: true }),
        navigator.mediaDevices.getUserMedia({ audio: true }),
      ]);

      // Stop the test streams immediately (we just needed permission)
      cameraPermission.getTracks().forEach((track) => track.stop());
      micPermission.getTracks().forEach((track) => track.stop());

      // Initialize audio components BEFORE connecting client
      await wavRecorder.begin();
      await wavStreamPlayer.connect();

      // Connect to the realtime API
      await client.connect();

      // Set up the session with proper audio configuration
      await client.updateSession({
        instructions: instructions,
        input_audio_transcription: { model: 'gpt-live-transcribe' },
        turn_detection: { type: 'server_vad' },
        // Important: Ensure audio output is enabled
        modalities: ['text', 'audio'],
        voice: 'alloy', // or 'verse', 'shimmer', etc.
        output_audio_format: 'pcm16',
        input_audio_format: 'pcm16',
      });

      client.on('input_audio_buffer.speech_stopped', () => {
        lastUserSpeechEndRef.current = Date.now();
      });

      client?.on('conversation.updated', async ({ item, delta }: any) => {
        const items = client.conversation.getItems();

        // 🎯 HANDLE AUDIO STREAM
        if (delta?.audio) {
          // If new response comes → interrupt previous audio
          if (currentAudioRef.current !== item.id) {
            currentAudioRef.current = item.id;

            try {
              await wavStreamPlayer.interrupt();
            } catch (error) {
              console.error('Interrupt error:', error);
            }
          }

          // Mark AI speaking
          setIsAISpeaking(true);
          setIsProcessingAudio(true);

          // Add AI audio to recording mix
          if (isRecording && delta.audio) {
            await addAIAudioToMix(delta.audio);
          }

          // Play audio
          wavStreamPlayer.add16BitPCM(delta.audio, item.id);
        }

        // WHEN RESPONSE COMPLETES
        if (item.status === 'completed') {
          // Reset speaking state
          setIsAISpeaking(false);
          setIsProcessingAudio(false);

          // Store transcript
          if (item.formatted?.transcript) {
            try {
              await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/transcripts`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sessionId,
                    role: item.role,
                    transcript: item.formatted.transcript,
                  }),
                }
              );
            } catch (error) {
              console.error('Error storing transcript:', error);
            }
          }
        }

        setItems(items);
      });

      client.on('response.audio_transcript.delta', (event: any) => {
        const items = client.conversation.getItems();
        const item = items.find((item: any) => item.id === event.item_id);
        if (item) {
          item.formatted.transcript =
            (item.formatted.transcript || '') + event.delta;
          setItems([...items]);
        }
      });

      // Handle response completion
      client.on('response.done', (event: any) => {
        const items = client.conversation.getItems();
        setItems([...items]);
      });

      // Initialize recording immediately
      await wavRecorder.record((data) => {
        if (clientRef.current?.isConnected()) {
          clientRef.current.appendInputAudio(data.mono);
        }
      });

      // Send resume content if available
      // if (resumeContent) {
      //   await client.sendUserMessageContent([
      //     {
      //       type: 'input_text',
      //       text: `${resumeContent}`, // Your resume content here
      //     },
      //   ]);
      // }

      if (resumeContent) {
        await client.sendUserMessageContent([
          {
            type: 'input_text',
            text: `
              System settings:
              Tool use: enabled.
              
              Resume content: 
              ${resumeContent}
              
              Instructions:
              - You are an **AI interviewer** responsible for conducting a **real-time, structured interview session** based on the provided resume content.
              - Use the candidate’s name (Record User in this case) to personalize the start of the interview. Example: "Let's begin the interview, Record User."
              - Conduct the interview in English.
              - Do not remind the candidate to respond in English when they are already speaking English.
              - If the candidate responds in a non-English language, briefly ask them to continue in English, then move on with the interview.
              - Do not ask any questions about the candidate's **name or email** during the interview.
              - This ensures a natural flow and pacing for the interview.
              - After the introduction, immediately proceed to the **Education section**, followed by Work Experience, Projects, Certifications, and Skills — ensuring every individual entry in each section is covered one by one.
              
              Mandatory Coverage Rule:
              - You must cover **each section** and **each individual item within every section (work experience, projects, certifications, skills)**.
              - No combining or skipping entries — **each work experience, project, certification, and skill must be discussed individually in the exact order they appear in the resume**.
              - Example for projects: First discuss "Web Project on Online Grievance Redressal System," then "Final Year Project On Digital Sketch To Photo Matching Algorithm," then "demo" — ensuring all are covered.
              
              Section Order & Coverage Process:
              1. Education
              2. Work Experience (each job individually)
              3. Projects (each project individually)
              4. Certifications (each certification individually)
              5. Skills (each skill individually)
              
              For each entry (job, project, certification, skill), follow this question flow:
              - Start with **basic context questions** (e.g., "Can you describe your role as a Software Engineer?")
              - Ask **at least one follow-up question** after every response.
              - Follow-ups should explore:
                  - Reasoning behind decisions and actions taken.
                  - Real-life challenges faced and how they were handled.
                  - Tools, technologies, and methods chosen — and why.
                  - Key lessons learned and what they would do differently now.
                  - How the experience applies to future work.
              - Ensure follow-ups are **dynamic and adaptive** based on the candidate's responses.
              
              Specific Section Guidance:
              
              Education Section:
              - Ask why they chose this field.
              - What subjects they found most challenging or rewarding.
              - How their academic work has helped in practical work.
              - Example: "You studied Computer Science — which programming concepts do you use most frequently in your work today?"
              
              Work Experience Section:
              - For each role (e.g., Software Engineer, IT Analyst, etc.), ask:
                  - What were your primary responsibilities?
                  - How did your role evolve over time?
                  - What tools, technologies, and frameworks were used, and why?
                  - What major challenges did you face, and how were they resolved?
                  - What impact did your work have on the business, team, or product?
              - Example: "In your IT Analyst role, how did you identify opportunities to reduce web designer time by 95%?"
              
              Projects Section:
              - For each project (including placeholder or demo projects), ask:
                  - What was the goal of the project?
                  - What was your personal role and contribution?
                  - What technologies or methodologies were used, and why?
                  - What challenges did you face during the project?
                  - If you were to redo this project, what would you change?
              - Example: "In the Final Year Project on Digital Sketch to Photo Matching, how did you design the matching algorithm and why?"
              
              Certifications Section:
              - For each certification, ask:
                  - Why did you pursue this certification?
                  - What were the key skills or knowledge gained?
                  - Which parts were most difficult, and how did you overcome them?
                  - How has this certification helped you in real-world work?
                  - Would you recommend this certification to others, and why?
              - Example: "How has the demo 1 certification helped you in your recent projects?"
              
              Skills Section:
              - For each skill (Communication Skills, Tableau, B2B Sales), ask:
                  - How did you develop this skill?
                  - Can you give a real-life example of using this skill at work?
                  - How do you keep this skill updated?
                  - How would you apply this skill to a new challenge in your next role?
              - Example: "You listed Tableau — can you walk me through a dashboard you built and what challenges you faced when visualizing data?"
              
              Handling Missing Sections:
              - If any section (Education, Projects, Certifications, etc.) is missing, acknowledge it briefly and ask:
                  - "Could you share any relevant experiences or information related to this section(mention section name) ?"
              
              Dynamic Follow-Ups (Mandatory):
              - Every response must trigger at least **one follow-up question**, exploring:
                  - Decisions made.
                  - Thought process.
                  - Problem-solving techniques.
                  - Tools, methods, and approaches used.
                  - Collaboration, leadership, and soft skills where relevant.
              
              Engagement Duration:
              - Ensure at least 15 minutes of interview time before concluding.
              - If all resume sections are exhausted, smoothly transition to **role-based and career-oriented general questions**, such as:
                  - How do you keep your technical skills updated?
                  - What’s a recent technical challenge you solved outside of work?
                  - What type of work culture or environment do you thrive in?
              
              Hints & Guidance:
              - If the candidate struggles, provide **a maximum of two hints**, never direct answers.
              - Example: "You might want to mention how you approached error handling in your page generation tool."
              
              Language Requirement:
              - Use English for your questions.
              - Only mention the English requirement after the candidate gives a non-English response.
              - Do not repeat "please respond in English" for normal English answers.
              
              Question Flow Control:
              - Strictly follow **forward-only flow** — once a question is asked, do not allow revisiting or re-answering previous questions.
              - Politely decline any request to return to earlier questions.
              
              Tone & Professionalism:
              - Maintain a **professional, structured, and evaluative tone**.
              - Avoid off-topic conversation.
              - Be polite but firm when enforcing:
                  - Forward flow.
                  - English-only rule only after a non-English response.
                  - Full section coverage.
              
              Complexity Level (Medium):
              - Target medium complexity, balancing:
                  - Basic factual recall.
                  - Thought process and reasoning.
                  - Process awareness (how they approached tasks).
                  - Applied knowledge (how they would improve the work today).
              - Example: "When working with high-volume data ingestion, how did you ensure the system could handle sudden spikes?"
              
              Overall Experience:
              - Ensure the interview feels like a **real-world professional evaluation**, not just a scripted Q&A.
              - Make sure the candidate feels they are being assessed on:
                  - Critical thinking.
                  - Technical and process understanding.
                  - Communication skills.
                  - Real-world application of knowledge.
              - Maintain **continuous flow with logical transitions**, ensuring all sections and entries are covered comprehensively.   
              
              - You are NOT a chatbot or assistant.
              - You must NEVER answer user questions or explain anything.
              - If the user asks a question, DO NOT answer it.
              - Instead say: "Let's stay focused on the interview."
              - Then immediately continue with the next interview question.

              - You must ALWAYS control the conversation.
              - The user is ONLY allowed to answer your questions.

              - Ask EXACTLY one question at a time.
              - After asking a question, STOP and wait for the user response.
              - Do NOT ask multiple questions in one response.
              - Do NOT continue speaking without user input.

              - Never switch into teaching mode.
              - Never provide explanations.
              - Never behave like ChatGPT assistant.

              Now begin the interview.`,
          },
        ]);
        await client.createResponse();
      }

      setIsConnected(true);
      setRealtimeEvents([]);
      setItems(client.conversation.getItems());
    } catch (error) {
      console.error('Error connecting:', error);

      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          alert(
            'Please allow access to both camera and microphone to start the interview.'
          );
        } else if (error.name === 'NotFoundError') {
          alert(
            'Camera or microphone not found. Please ensure your devices are properly connected.'
          );
        } else {
          alert(
            'Error accessing camera or microphone. Please check your device settings.'
          );
        }
      } else {
        alert(
          'Connection failed. Please check your internet connection and try again.'
        );
      }

      await disconnectConversation();
    }
  }, [isConnected, instructions, resumeContent]);

  useEffect(() => {
    if (eventsScrollRef.current) {
      const eventsEl = eventsScrollRef.current;
      const scrollHeight = eventsEl.scrollHeight;
      // Only scroll if height has just changed
      if (scrollHeight !== eventsScrollHeightRef.current) {
        eventsEl.scrollTop = scrollHeight;
        eventsScrollHeightRef.current = scrollHeight;
      }
    }
  }, [realtimeEvents]);

  /**
   * Core RealtimeClient and audio capture setup
   * Set all of our instructions, tools, events and more
   */
  useEffect(() => {
    // Get refs
    const wavStreamPlayer = wavStreamPlayerRef.current;
    const client = clientRef.current;

    // Set instructions
    // client?.updateSession({ instructions: instructions });
    // client?.updateSession({ voice: 'alloy' });
    // // Set transcription, otherwise we don't get user transcriptions back
    // client?.updateSession({
    //   input_audio_transcription: { model: 'whisper-1' },
    // });

    // Add tools
    client?.addTool(
      {
        name: 'set_memory',
        description: 'Saves important data about the user into memory.',
        parameters: {
          type: 'object',
          properties: {
            key: {
              type: 'string',
              description:
                'The key of the memory value. Always use lowercase and underscores, no other characters.',
            },
            value: {
              type: 'string',
              description: 'Value can be anything represented as a string',
            },
          },
          required: ['key', 'value'],
        },
      },
      async ({ key, value }: { [key: string]: any }) => {
        setMemoryKv((memoryKv) => {
          const newKv = { ...memoryKv };
          newKv[key] = value;
          return newKv;
        });
        return { ok: true };
      }
    );

    // Add conduct_interview tool
    client?.addTool(
      {
        name: 'conduct_interview',
        description:
          'Conducts an AI-powered interview based on resume content for a specified duration.',
        parameters: {
          type: 'object',
          properties: {
            resume_content: {
              type: 'string',
              description:
                'The content of the resume that will be the basis for the interview questions.',
            },
            duration: {
              type: 'number',
              description:
                'Duration of the interview in minutes. Default is 15 minutes if not specified.',
              default: 15,
            },
          },
          required: ['resume_content'],
        },
      },
      async ({
        resume_content,
        duration = 10,
      }: {
        resume_content: string;
        duration: number;
      }) => {
        try {
          setResumeContent(resume_content);

          // Return initial success response
          return {
            ok: true,
            message: 'Interview session initiated',
            interviewStatus: 'ready',
          };
        } catch (error) {
          console.error('Error setting up interview:', error);
          return {
            ok: false,
            error: 'Failed to initialize interview session',
          };
        }
      }
    );

    client?.on('realtime.event', (realtimeEvent: RealtimeEvent) => {
      setRealtimeEvents((realtimeEvents) => {
        const lastEvent = realtimeEvents[realtimeEvents.length - 1];
        if (lastEvent?.event.type === realtimeEvent.event.type) {
          lastEvent.count = (lastEvent.count || 0) + 1;
          return realtimeEvents.slice(0, -1).concat(lastEvent);
        } else {
          return realtimeEvents.concat(realtimeEvent);
        }
      });
    });
    client?.on('error', (event: any) => console.error(event));
    client?.on('conversation.interrupted', async () => {
      const trackSampleOffset = await wavStreamPlayer.interrupt();
      if (trackSampleOffset?.trackId) {
        const { trackId, offset } = trackSampleOffset;
        await client.cancelResponse(trackId, offset);
      }
    });

    setItems(client?.conversation.getItems() ?? []);

    return () => {
      // cleanup; resets to defaults
      client?.reset();
    };
  }, []);

  //These is for timer start to
  const [timeLeft, setTimeLeft] = useState(600); // 15 minutes in seconds
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (isConnected) {
      timer = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }

    if (timeLeft === 0 && isConnected) {
      setIsConnected(false); // Disconnect when timer hits 0
    }

    return () => {
      if (timer) {
        clearInterval(timer); // Cleanup interval
      }
    };
  }, [isConnected, timeLeft]);

  const getMinutes: number = Math.floor(timeLeft / 60);
  const getSeconds: number = timeLeft % 60;
  const disconnectConversation = useCallback(async () => {
    try {
      if (isRecording) {
        await stopRecordingAndUpload();
      }

      axios
        .post(
          `${process.env.REACT_APP_API_BASE_URL}/api/recordings/mergeChunks`,
          {
            studentId,
            sessionId,
          }
        )
        .catch((err) => {
          console.error('Merge failed to trigger:', err);
        });

      const client = clientRef.current;
      const wavRecorder = wavRecorderRef.current;
      const wavStreamPlayer = wavStreamPlayerRef.current;

      // Capture the total duration directly before disconnecting
      const calculatedDuration = 600 - timeLeft; // Ensure you're using the latest timeLeft

      // Optional: Store totalDuration or make API request to save it
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/transcripts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          studentId,
          totalDuration: calculatedDuration, // Send the calculated total duration
        }),
      });

      // console.log('Executed API request successfully');

      // Stop recorder first so the audio callback stops before WebSocket closes
      await wavRecorder.end();
      client?.disconnect();
      await wavStreamPlayer.interrupt();

      // Clear session and other relevant state
      setIsConnected(false);
      setRealtimeEvents([]);
      setItems([]);
      setMemoryKv({});

      if (jobId) {
        navigate(`/feedback/${sessionId}?job_id=${jobId}`, {
          replace: true,
          state: { sessionId },
        });
      } else {
        navigate(`/feedback/${sessionId}`, {
          replace: true,
          state: { sessionId },
        });
      }

    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  }, [navigate, timeLeft, isRecording, studentId, sessionId]);

  useEffect(() => {
    return () => {
      // Clean up MediaRecorder and streams when component unmounts
      const { mediaRecorder, videoStream } = mediaRecorderRef.current;

      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
      }

      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (timeLeft === 0 && isConnected) {
      disconnectConversation(); // Trigger disconnection when timer ends
      return; // Stop further execution
    }
  }, [timeLeft, isConnected, disconnectConversation]);
  const [speakingRole, setSpeakingRole] = useState<'assistant' | 'user' | null>(
    null
  );

  /**
   * Update the progress bar based on time left
   * This effect will run every time timeLeft changes
   * It will update the progress bar based on the time left
   */
  useEffect(() => {
    const canvas = document.getElementById(
      'progressCanvas'
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const radius = 11; // Radius of the circle
    const lineWidth = 5; // Width of the progress bar
    const totalSeconds = 600; // Total time (5 minutes)

    // Calculate progress percentage
    const percentage = timeLeft / totalSeconds;

    // Determine color based on percentage
    let color = '#52c41a'; // Green
    if (percentage <= 0.67 && percentage > 0.34) color = '#faad14'; // Yellow
    if (percentage <= 0.34) color = '#ff4d4f'; // Red

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background circle
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#e0e0e0'; // Gray background
    ctx.lineWidth = lineWidth;
    ctx.stroke();

    // Draw progress arc
    ctx.beginPath();
    ctx.arc(
      canvas.width / 2,
      canvas.height / 2,
      radius,
      -Math.PI / 2, // Start at top
      -Math.PI / 2 + Math.PI * 2 * percentage, // End based on percentage
      false
    );
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [timeLeft]);

  const [showendmessage, setShowendmessage] = useState(false);
  const handleStopClick = () => {
    setShowendmessage((prev) => !prev);
  };

  interface MediaDevice {
    deviceId: string;
    label: string;
  }

  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [audioInputDevices, setAudioInputDevices] = useState<MediaDevice[]>([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState<MediaDevice[]>(
    []
  );

  // Selected devices
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
  const [selectedAudioInputDevice, setSelectedAudioInputDevice] =
    useState<string>('');
  const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] =
    useState<string>('');

  // Stream and video reference
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

  // Initialize devices
  const initializeDevices = async () => {
    try {
      // Request device permissions
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });

      // Enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Categorize devices
      const videoInputs = devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 5)}`,
        }));

      const audioInputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
        }));

      const audioOutputs = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
        }));

      // Update state
      setVideoDevices(videoInputs);
      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);

      // Auto-select first devices if available
      if (videoInputs.length) setSelectedVideoDevice(videoInputs[0].deviceId);
      if (audioInputs.length)
        setSelectedAudioInputDevice(audioInputs[0].deviceId);
      if (audioOutputs.length)
        setSelectedAudioOutputDevice(audioOutputs[0].deviceId);
    } catch (error) {
      console.error('Device initialization error:', error);
    }
  };

  // Start media stream
  const startMediaStream = async () => {
    try {
      // Stop existing stream
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }

      // Create new stream
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: selectedVideoDevice },
        audio: { deviceId: selectedAudioInputDevice },
      });

      setMediaStream(newStream);

      // Attach stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
      }
    } catch (error) {
      console.error('Stream start error:', error);
    }
  };

  // Change device
  const changeDevice = async (
    deviceType: 'video' | 'audio',
    deviceId: string
  ) => {
    if (deviceType === 'video') setSelectedVideoDevice(deviceId);
    if (deviceType === 'audio') setSelectedAudioInputDevice(deviceId);
    await startMediaStream();
  };

  // Cleanup on unmount
  useEffect(() => {
    initializeDevices();
    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // Start stream when devices are selected
  useEffect(() => {
    if (selectedVideoDevice && selectedAudioInputDevice) {
      startMediaStream();
    }
  }, [selectedVideoDevice, selectedAudioInputDevice]);

  const [isChecking, setIsChecking] = useState(false);
  const handleOpenThis = () => {
    // console.log('I was clicked');
    setIsChecking((prev) => {
      // console.log('New state:', !prev);
      return !prev;
    });
  };

  //audio render
  useEffect(() => {
    const audio = new Audio('/testingspeaker.mp3'); // Ensure the path is correct

    const timer = setTimeout(() => {
      setShowPopup(true);
      audio
        .play()
        .catch((error) => console.error('Audio playback error:', error));
    }, 1000);

    return () => {
      clearTimeout(timer);
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (items.length) {
      const lastItem = items[items.length - 1];
      setSpeakingRole(
        lastItem.role === 'assistant' || lastItem.role === 'user'
          ? lastItem.role
          : null
      );
    }
  }, [items]);

  const [showpopup, setShowPopup] = useState(true);

  const handlestartinterview = () => {
    startRecording();
    connectConversation();
    setShowPopup((prev) => !prev);
  };

  let currentAudio: HTMLAudioElement | null = null;
  const testSpeaker = async (deviceId: string) => {
    if (!deviceId) return;

    try {
      // Stop and reset the previously playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      // Create a new audio instance
      const audio = new Audio('/testingspeaker.mp3'); // Ensure this file exists
      audio.volume = 0.8;

      if ('setSinkId' in audio) {
        await (audio as any).setSinkId(deviceId);
        // console.log(`Output device set to: ${deviceId}`);
      } else {
        console.warn('setSinkId() is not supported in this browser.');
      }

      currentAudio = audio; // Store the current audio instance
      await audio.play(); // Play test sound
    } catch (error) {
      console.error('Error playing test sound:', error);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      message.warning('Feedback cannot be empty.');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/user-query`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId, // Sending the generated sessionId
            content: feedback, // Sending user-typed feedback
          }),
        }
      );

      if (response.ok) {
        message.success('Feedback submitted successfully!');
        setFeedback(''); // Clear feedback after submission
        setIsModalOpen(false); // Close modal after successful submission
      } else {
        throw new Error('Failed to submit feedback');
      }
    } catch (error) {
      message.error('Error submitting feedback. Please try again.');
    }
  };

  return (
    <div className="h-screen bg-[#232323] text-white  ">
      {isUploading && (
        <div className="loading-indicator">
          <p>Uploading video...</p>
          <div className="spinner"></div>
        </div>
      )}

      {/* Initial mic check popup */}
      <Modal
        open={showpopup}
        footer={null}
        centered
        closable={false}
        className="rounded-2xl shadow-lg"
      >
        <div className="flex flex-col justify-center items-center p-6">
          <h2 className="text-lg font-semibold text-gray-900 text-center">
            Quick Speaker Check
          </h2>

          <p className="text-sm text-gray-500 text-center mt-2">
            GoodFreshers AI is speaking...
          </p>

          <div className="w-full mt-3 h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute left-0 h-1.5 bg-blue-500 rounded-full w-1/3 animate-bounce-progress"></div>
          </div>

          <div className="flex w-full mt-6 space-x-4">
            <button
              onClick={handlestartinterview}
              className="bg-[#022c22] hover:[#022c22] text-white text-sm font-medium py-2 px-4 rounded-lg w-full"
            >
              {' '}
              Yes, I can hear you
            </button>

            <Link to="https://app.goodfreshers/tools" className="w-full">
              <button className="bg-gray-200 text-gray-600 text-sm font-medium py-2 px-4 rounded-lg w-full">
                Try Rejoin
              </button>
            </Link>
          </div>
        </div>
      </Modal>

      <div className="Header">
        <div className="webcam-wrapper">
          <WebCam
            onAlertCountReached={handleAlertCount}
            speakingRole={speakingRole}
          />
        </div>
      </div>
      <div className="main ">
        <div className=" flex flex-col text-center justify-center items-center">
          <div className=" flex justify-center items-center space-x-1">
            <div>
            <Sparkle className="w-[10.21px] h-3" />
            </div>
            <p className="leading-[10.93px] no-underline  text-[#FFFFFF] text-md ">
              Question Asked
            </p>
          </div>
          <div
            className="w-[823px] h-[102px] flex items-center justify-center mt-2 px-4 text-center"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: '400' }}
          >
            {/* Filter to only show assistant items */}
            {items
              .filter(
                (conversationItem) => conversationItem.role === 'assistant'
              ) // Show only assistant
              .slice(-1) // Show only the latest message from assistant
              .map((conversationItem) => (
                <div
                  key={conversationItem.id}
                  className="max-w-full text-justify leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif', fontWeight: '500' }}
                >
                  <p className="text-sm">
                    {conversationItem.formatted.transcript ||
                      conversationItem.formatted.text ||
                      'Listening...'}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="Footer">
        <div className="w-full absolute bottom-0 left-0  ">
          <div className=" flex justify-between p-8 items-center">
            <div className=" flex justify-center items-center">
              <p className=" font-medium">Self Assessment</p>
              <div className="flex space-x-3 p-4 rounded-lg">
                {isChecking && (
                  <div className="mx-auto w-auto h-auto absolute bottom-20 left-48 p-6 bg-white rounded-lg shadow-lg space-y-6">
                    <div className=" flex justify-between">
                      <h2 className="text-xl font-semibold text-gray-800">
                        Settings
                      </h2>
                      <X
                        style={{ color: '#8F8F8F', cursor: 'pointer' }}
                        onClick={handleOpenThis}
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Speaker
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./audio.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />
                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                            onChange={(e) => testSpeaker(e.target.value)}
                          >
                            {audioOutputDevices.length > 0 &&
                              audioOutputDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Microphone
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./mic.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />

                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                          >
                            {audioInputDevices.length > 0 &&
                              audioInputDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>

                      {/* Video */}
                      <div>
                        <label className="block text-sm font-medium text-blueai mb-1">
                          Video
                        </label>
                        <div className="relative z-10 flex items-center">
                          <img
                            src="./camera.png"
                            alt="Icon"
                            className="absolute left-3"
                            style={{ width: '30px', height: '30px' }}
                          />

                          <select
                            style={{ color: '#000000' }}
                            className="w-full pl-11 pr-4 py-2 border rounded-lg"
                          >
                            {' '}
                            {videoDevices.length > 0 &&
                              videoDevices.map((device) => (
                                <option
                                  key={device.deviceId}
                                  value={device.deviceId}
                                >
                                  {device.label || 'Unknown Device'}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Settings Icon */}
                <button
                  className="w-10 h-10 flex items-center  justify-center rounded-full cursor-pointer z-10 focus:outline-none"
                  onClick={handleOpenThis}
                >
                  <Settings className="w-6 h-6 text-white " />
                </button>

                {/* Warning Icon */}

                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer z-10"
                  onClick={() => setIsModalOpen(true)}
                >
                  <AlertOctagon className="w-6 h-6 text-white" />
                </button>

                {/* Feedback Modal */}
                <Modal
                  open={isModalOpen}
                  footer={null}
                  centered
                  closable={false}
                  className="rounded-2xl shadow-lg"
                  onCancel={() => setIsModalOpen(false)}
                >
                  <div className="flex flex-col justify-center items-center p-6 w-full max-w-md">
                    <h2 className="text-lg font-semibold text-gray-900 text-center">
                      Feedback
                    </h2>
                    <textarea
                      className="w-full mt-4 p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter your feedback here..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                    />
                    <button
                      className="mt-4 bg-[#022c22] hover:bg-[#022c22] text-white text-sm font-medium py-2 px-4 rounded-lg w-full"
                      onClick={handleSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </Modal>
              </div>

              {/* End message popup */}

              <Modal
                open={showendmessage}
                footer={null}
                centered
                closable={false}
                className="rounded-2xl shadow-lg"
              >
                <div className="flex flex-col justify-center items-center p-8">
                  <h2 className="text-lg font-bold text-gray-900 text-center">
                    Are you sure you want to end?
                  </h2>

                  <p className="text-sm text-gray-500 text-center mt-2">
                    You cannot resume if you end this call. <br /> But you can
                    retake anytime.
                  </p>

                  <div className="flex justify-center items-center mt-6 space-x-4">
                    <button
                      onClick={disconnectConversation}
                      className="bg-[#022c22] hover:bg-[#022c22] cursor-pointer text-white text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      Yes, I’m sure
                    </button>

                    <button
                      onClick={handleStopClick}
                      className="bg-gray-100 hover:bg-gray-200 cursor-pointer text-gray-700 text-sm font-medium py-2 px-4 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Modal>
            </div>
            <div className="flex justify-center items-center space-x-4 ">
              <img src="/frame3.png"></img>
              <p className="text-md ">GoodFreshers AI</p>
              <img
                src="/wave.png"
                className={`absolute bottom-0 opacity-100 `}
                alt="Wave visualization"
              />
            </div>
            <div className="flex justify-center items-center p-4 space-x-2 text-white">
              <div className="flex items-center space-x-2">
                <p className=" font-medium"> Remaining Time |</p>
                <p className="font-mono">
                  {`${getMinutes.toString().padStart(2, '0')}:${getSeconds
                    .toString()
                    .padStart(2, '0')}`}
                </p>
                <canvas
                  id="progressCanvas"
                  width="35"
                  height="35"
                  className="rounded-lg shadow-md"
                ></canvas>
                <div>
                  <button
                    className="flex items-center justify-center w-20 h-10 bg-red-600 rounded-full hover:bg-red-700 transition duration-300 "
                    onClick={handleStopClick}
                  >
                    <img src="/phone-icon.png " alt="End Button" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
